import React, { memo, useMemo } from 'react';
import { Flex, Text } from '@adobe/react-spectrum';
import moment from 'moment';
import {
  DAY_WIDTH_PX,
  WEEKDAY_COLOR,
  WEEKEND_COLOR
} from '../utils/monthlyLayoutConstants';
import { getVisibleDayColumns } from '../utils/getVisibleDayColumns';
import type { MonthDescriptor } from '../utils/types';
import styles from '../MonthlyRoadmap.module.scss';

interface DayHeaderRowProps {
  month: MonthDescriptor;
  isExpanded: boolean;
  isCompact: boolean;
}

export const DayHeaderRow = memo(
  ({ month, isExpanded, isCompact }: DayHeaderRowProps) => {
    const visibleDays = useMemo(
      () => getVisibleDayColumns(month.daysInMonth, isExpanded, isCompact),
      [month.daysInMonth, isExpanded, isCompact]
    );

    return (
      <Flex
        data-testid={`monthly-day-header-row-${month.key}`}
        direction="row"
        UNSAFE_className={styles.dayHeaderRow}
      >
        {visibleDays.map(day => {
          const dayOfWeek = moment({
            year: month.year,
            month: month.month - 1,
            day
          }).day();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return (
            <Flex
              key={day}
              alignItems="center"
              justifyContent={isExpanded ? 'center' : 'center'}
              UNSAFE_className={styles.dayHeaderCell}
              UNSAFE_style={{
                width: `${DAY_WIDTH_PX}px`,
                minWidth: `${DAY_WIDTH_PX}px`
              }}
            >
              {isExpanded ? (
                <Text
                  UNSAFE_className={styles.dayHeaderText}
                  UNSAFE_style={{
                    color: isWeekend ? WEEKEND_COLOR : WEEKDAY_COLOR
                  }}
                >
                  {day}
                </Text>
              ) : (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  UNSAFE_style={{
                    width: '50%',
                    height: '100%'
                  }}
                >
                  <Text
                    UNSAFE_className={styles.dayHeaderText}
                    UNSAFE_style={{
                      color: isWeekend ? WEEKEND_COLOR : WEEKDAY_COLOR
                    }}
                  >
                    {day}
                  </Text>
                </Flex>
              )}
            </Flex>
          );
        })}
      </Flex>
    );
  }
);
